/**
 * Emission Source Breakdown Table
 *
 * Pivot of CO₂e across (emission source × time period) for the selected FY.
 * Rows are sub-sources grouped under activity-type header rows. Columns are
 * dynamic based on granularity (Monthly / Quarterly / Annual). Cells are
 * prorated CO₂e totals computed server-side.
 */

import { useMemo, useState } from 'react';
import { Table } from 'reactstrap';
import { Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { TabNav } from '@g17eco/ui-modern/components/ui/tab-nav';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { useGetEmissionsBySourcePeriodQuery } from '../api/emissions';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';
import { convertEmissionValue } from '../utils/emissionsFormatter';
import { resolveSourceLabel } from '../utils/resolveSourceLabel';
import { resolveSourceIcon } from '../utils/resolveSourceIcon';
import {
  ACTIVITY_TYPE_COLORS,
  ACTIVITY_TYPE_FA_ICONS,
  formatActivityType,
} from '../config/activityTypeBranding';
import type {
  EmissionScope,
  SourcePeriodGranularity,
  SourcePeriodRow,
} from '../types/emissions';

interface EmissionSourceBreakdownTableProps {
  initiativeId: string;
  /** FY start as ISO date string (YYYY-MM-DD). */
  periodStart: string;
  /** FY end as ISO date string (YYYY-MM-DD). */
  periodEnd: string;
  /** Leave undefined to show all scopes. */
  scope?: EmissionScope;
  includeChildren?: boolean;
}

interface GroupedRows {
  activityType: string;
  rows: SourcePeriodRow[];
  /** Sum of grandTotal across rows in this group — used to decide ordering. */
  groupTotal: number;
}

type ViewMode = 'total' | 'delta';

export const EmissionSourceBreakdownTable = ({
  initiativeId,
  periodStart,
  periodEnd,
  scope,
  includeChildren,
}: EmissionSourceBreakdownTableProps) => {
  const [granularity, setGranularity] = useState<SourcePeriodGranularity>('monthly');
  const [viewMode, setViewMode] = useState<ViewMode>('total');
  const { settings } = useEmissionsFormatter();

  const { data, isLoading, isFetching } = useGetEmissionsBySourcePeriodQuery(
    {
      initiativeId,
      periodStart,
      periodEnd,
      granularity,
      scopes: scope ? [scope] : undefined,
      includeChildren,
    },
    { skip: !initiativeId },
  );

  const groups = useMemo<GroupedRows[]>(() => {
    if (!data?.rows?.length) return [];

    const byType = new Map<string, SourcePeriodRow[]>();
    for (const row of data.rows) {
      const existing = byType.get(row.activityType);
      if (existing) {
        existing.push(row);
      } else {
        byType.set(row.activityType, [row]);
      }
    }

    const grouped: GroupedRows[] = [];
    for (const [activityType, rows] of byType.entries()) {
      const groupTotal = rows.reduce((s, r) => s + r.grandTotal, 0);
      if (groupTotal <= 0) continue;
      // Within a group, sort sub-rows by grandTotal desc; group-level (no
      // sub-row) rows are left as-is.
      const sortedRows = [...rows].sort((a, b) => b.grandTotal - a.grandTotal);
      grouped.push({ activityType, rows: sortedRows, groupTotal });
    }
    // Groups ordered by total CO₂e desc so the biggest emitters appear first.
    grouped.sort((a, b) => b.groupTotal - a.groupTotal);
    return grouped;
  }, [data]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className='tw:p-6 tw:flex tw:justify-center'>
          <Loader2 className='tw:size-5 tw:animate-spin tw:text-muted-foreground' />
        </CardContent>
      </Card>
    );
  }

  // Hide entirely when there's no data for the selected FY+scope+includeChildren.
  if (!data?.buckets?.length || groups.length === 0) {
    return null;
  }

  const buckets = data.buckets;

  // Every cell is formatted in the user's display unit (e.g. tCO₂e) but
  // *individually* suffixed with k / M / B when the value would otherwise have
  // too many digits to read. A cell showing "201" and another showing "41.7k"
  // are directly comparable at a glance.
  const baseUnitLabel = BASE_UNIT_LABELS[settings.displayUnit];
  const format = (kg: number): string => {
    if (!(kg > 0)) return '';
    return formatScaledValue(convertEmissionValue(kg, settings.displayUnit));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <div className='tw:flex tw:justify-between tw:items-center'>
            <div>
              Emission Sources ({viewMode === 'delta' ? 'Δ%' : baseUnitLabel})
            </div>
            <div className='tw:flex tw:items-center tw:gap-3'>
              {isFetching && (
                <Loader2 className='tw:size-4 tw:animate-spin tw:text-muted-foreground' />
              )}
              <TabNav
                variant='toggle'
                size='xs'
                activeTab={viewMode}
                onChange={(v) => setViewMode(v as ViewMode)}
                tabs={[
                  { key: 'total', label: 'Total' },
                  { key: 'delta', label: 'Δ vs prev' },
                ]}
              />
              <TabNav
                variant='toggle'
                size='xs'
                activeTab={granularity}
                onChange={(v) => setGranularity(v as SourcePeriodGranularity)}
                tabs={[
                  { key: 'monthly', label: 'Monthly' },
                  { key: 'quarterly', label: 'Quarterly' },
                  { key: 'annual', label: 'Annual' },
                ]}
              />
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table hover size='sm' className='tw:mb-0 tw:table-fixed tw:w-full tw:text-sm'>
          <colgroup>
            <col style={SOURCE_COLUMN_STYLE} />
            {buckets.map((b) => (
              <col key={b.index} />
            ))}
            <col />
          </colgroup>
          <thead>
            <tr>
              <th></th>
              {buckets.map((b) => (
                <th key={b.index} className='tw:text-right'>
                  {b.label}
                </th>
              ))}
              <th className='tw:text-right'>
                {viewMode === 'delta' ? 'Δ Total' : 'Total'}
              </th>
            </tr>
          </thead>
            <tbody>
              {groups.map((group) => (
                <GroupBlock
                  key={group.activityType}
                  group={group}
                  bucketCount={buckets.length}
                  format={format}
                  unitLabel={baseUnitLabel}
                  viewMode={viewMode}
                />
              ))}
          </tbody>
        </Table>
      </CardContent>
    </Card>
  );
};

interface GroupBlockProps {
  group: GroupedRows;
  bucketCount: number;
  format: (kg: number) => string;
  unitLabel: string;
  viewMode: ViewMode;
}

/** Red for increases (more emissions = bad), green for decreases. */
const DELTA_UP_COLOR = '#dc2626';
const DELTA_DOWN_COLOR = '#16a34a';
/** Positive/negative percent thresholds past which we saturate the heatmap. */
const DELTA_SATURATION_PCT = 100;

interface DeltaCell {
  /** Relative change (e.g. 0.125 = +12.5%). `null` when undefined (no prev). */
  ratio: number | null;
  /** Marks "new" — prev was zero but current is non-zero. */
  isNew: boolean;
  /** Marks "gap" — prev had data but current is zero. */
  isGap: boolean;
}

const computeDeltas = (totals: number[]): DeltaCell[] =>
  totals.map((v, i) => {
    if (i === 0) return { ratio: null, isNew: false, isGap: false };
    const prev = totals[i - 1];
    if (prev <= 0 && v <= 0) return { ratio: null, isNew: false, isGap: false };
    if (prev <= 0 && v > 0) return { ratio: null, isNew: true, isGap: false };
    if (prev > 0 && v <= 0) return { ratio: -1, isNew: false, isGap: true };
    return { ratio: (v - prev) / prev, isNew: false, isGap: false };
  });

/**
 * Overall Δ from the first non-empty bucket to the last non-empty bucket in a
 * row. Used in delta mode's Total column so users can see the net change
 * across the whole FY at a glance.
 */
const computeSpanDelta = (totals: number[]): DeltaCell => {
  const firstIdx = totals.findIndex((v) => v > 0);
  if (firstIdx === -1) return { ratio: null, isNew: false, isGap: false };
  let lastIdx = -1;
  for (let i = totals.length - 1; i >= 0; i--) {
    if (totals[i] > 0) { lastIdx = i; break; }
  }
  // Only one non-empty bucket → no meaningful delta to show.
  if (lastIdx === firstIdx) return { ratio: 0, isNew: false, isGap: false };
  const first = totals[firstIdx];
  const last = totals[lastIdx];
  return { ratio: (last - first) / first, isNew: false, isGap: false };
};

const formatDelta = (cell: DeltaCell): string => {
  if (cell.isNew) return 'new';
  if (cell.ratio === null) return '';
  const pct = cell.ratio * 100;
  const rounded = Math.round(pct * 10) / 10;
  const sign = rounded > 0 ? '+' : '';
  return `${sign}${rounded.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

/**
 * Background tint for a Δ% cell. Magnitude scaled against a saturation point
 * so typical month-over-month swings are visible and extreme swings max out.
 * Up = red (anomaly toward more emissions), down = green.
 */
const deltaHeatmapStyle = (cell: DeltaCell): React.CSSProperties | undefined => {
  if (cell.isNew) {
    return { backgroundColor: hexWithAlpha(DELTA_UP_COLOR, 0.22) };
  }
  if (cell.ratio === null) return undefined;
  const pct = cell.ratio * 100;
  const magnitude = Math.min(Math.abs(pct) / DELTA_SATURATION_PCT, 1);
  const color = pct >= 0 ? DELTA_UP_COLOR : DELTA_DOWN_COLOR;
  return { backgroundColor: hexWithAlpha(color, magnitude * 0.35) };
};

const HEATMAP_MAX_ALPHA = 0.15;

/**
 * Fixed width for the emission-source column so long labels (e.g. "HVO
 * (Hydrotreated Vegetable Oil)") truncate with an ellipsis rather than
 * wrapping to a second line or stretching the column past reasonable width.
 */
const SOURCE_COLUMN_STYLE: React.CSSProperties = { width: 200, maxWidth: 200 };

/** Base unit label shown once in the group header (e.g. "tCO₂e"). */
const BASE_UNIT_LABELS: Record<'kgCO2e' | 'tCO2e' | 'short_ton', string> = {
  kgCO2e: 'kgCO₂e',
  tCO2e: 'tCO₂e',
  short_ton: 'short tons CO₂e',
};

/**
 * Format a value (already converted to the user's display unit) with an
 * inline SI suffix — "k" for thousands, "M" for millions, "B" for billions.
 * Preserves one decimal for readability. Small values (<1) show two decimals
 * so they don't collapse to "0.0".
 */
const formatScaledValue = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) {
    return `${toFixed(value / 1_000_000_000, 1)}B`;
  }
  if (abs >= 1_000_000) {
    return `${toFixed(value / 1_000_000, 1)}M`;
  }
  if (abs >= 1_000) {
    return `${toFixed(value / 1_000, 1)}k`;
  }
  if (abs < 1 && abs > 0) {
    return toFixed(value, 2);
  }
  return toFixed(value, 1);
};

const toFixed = (n: number, dp: number): string =>
  n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });

/**
 * Background tint for a cell based on its share of the group's max cell
 * value. Returns `undefined` for zero / missing values so empty cells stay
 * transparent.
 */
const heatmapStyle = (value: number, max: number, color: string): React.CSSProperties | undefined => {
  if (!(value > 0) || !(max > 0)) return undefined;
  const alpha = (value / max) * HEATMAP_MAX_ALPHA;
  return { backgroundColor: hexWithAlpha(color, alpha) };
};

const hexWithAlpha = (hex: string, alpha: number): string => {
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${a}`;
};

const GroupBlock = ({ group, bucketCount, format, viewMode }: GroupBlockProps) => {
  const color = ACTIVITY_TYPE_COLORS[group.activityType] ?? '#94a3b8';
  const icon = ACTIVITY_TYPE_FA_ICONS[group.activityType] ?? 'fa-leaf';

  // Per-group max across all body cells (bucket columns, excluding Total)
  // — scales the `total` heatmap within each group independently.
  const groupMax = group.rows.reduce((m, r) => {
    for (const v of r.totals) if (v > m) m = v;
    return m;
  }, 0);

  const renderBodyCells = (row: SourcePeriodRow) => {
    if (viewMode === 'delta') {
      const deltas = computeDeltas(row.totals);
      return row.totals.map((v, i) => {
        // First column has no previous bucket to compare against — show the
        // absolute value instead so the row has a starting point to read from.
        if (i === 0) {
          return (
            <td key={i} className='tw:text-right tw:text-xs' style={heatmapStyle(v, groupMax, color)}>
              {v > 0 ? format(v) : ''}
            </td>
          );
        }
        return (
          <td key={i} className='tw:text-right tw:text-xs' style={deltaHeatmapStyle(deltas[i])}>
            {formatDelta(deltas[i])}
          </td>
        );
      });
    }
    return row.totals.map((v, i) => (
      <td key={i} className='tw:text-right tw:text-xs' style={heatmapStyle(v, groupMax, color)}>
        {v > 0 ? format(v) : ''}
      </td>
    ));
  };

  // Group-level activity types (purchased_*, Scope 3 v1) produce exactly one
  // row with subTypeField === null — render it as a single bold row at the
  // group position rather than a header + empty body.
  const onlyRow = group.rows.length === 1 && group.rows[0].subTypeField === null
    ? group.rows[0]
    : null;

  if (onlyRow) {
    const { groupLabel } = resolveSourceLabel(
      onlyRow.activityType,
      onlyRow.subTypeField,
      onlyRow.subTypeValue,
    );
    return (
      <tr>
        <td className='tw:font-semibold tw:whitespace-nowrap' style={SOURCE_COLUMN_STYLE} title={groupLabel}>
          <GroupIcon color={color} icon={icon} />
          <span className='tw:truncate tw:inline-block tw:align-middle tw:text-xs tw:max-w-[200px]'>{groupLabel}</span>
        </td>
        {renderBodyCells(onlyRow)}
        <td
          className='tw:text-right tw:font-semibold tw:text-xs'
          style={viewMode === 'delta' ? deltaHeatmapStyle(computeSpanDelta(onlyRow.totals)) : undefined}
        >
          {viewMode === 'delta'
            ? formatDelta(computeSpanDelta(onlyRow.totals))
            : format(onlyRow.grandTotal)}
        </td>
      </tr>
    );
  }

  return (
    <>
      <tr className={cn('background-ThemeBgLight')}>
        <td className='tw:font-semibold tw:whitespace-nowrap tw:text-xs' colSpan={bucketCount + 2}>
          <GroupIcon color={color} icon={icon} />
          {formatActivityType(group.activityType)}
        </td>
      </tr>
      {group.rows.map((row) => {
        const { sourceLabel } = resolveSourceLabel(
          row.activityType,
          row.subTypeField,
          row.subTypeValue,
        );
        const iconInfo = resolveSourceIcon(row.subTypeField, row.subTypeValue);
        return (
          <tr key={`${row.scope}|${row.subTypeField}|${row.subTypeValue}`}>
            <td
              className='tw:pl-4'
              style={SOURCE_COLUMN_STYLE}
              title={sourceLabel ?? 'Unspecified'}
            >
              <span className='tw:flex tw:items-center tw:gap-2 tw:overflow-hidden'>
                {iconInfo ? <SubRowIcon info={iconInfo} /> : <span className='tw:w-6 tw:h-6 tw:shrink-0' />}
                <span className='tw:truncate tw:whitespace-nowrap'>
                  {sourceLabel ?? 'Unspecified'}
                </span>
              </span>
            </td>
            {renderBodyCells(row)}
            <td
              className='tw:text-right tw:font-medium tw:text-xs'
              style={viewMode === 'delta' ? deltaHeatmapStyle(computeSpanDelta(row.totals)) : undefined}
            >
              {viewMode === 'delta'
                ? formatDelta(computeSpanDelta(row.totals))
                : format(row.grandTotal)}
            </td>
          </tr>
        );
      })}
    </>
  );
};

const GroupIcon = ({ color, icon }: { color: string; icon: string }) => (
  <span
    className='tw:inline-flex tw:items-center tw:justify-center tw:rounded tw:mr-2'
    style={{ width: 20, height: 20, backgroundColor: `${color}22` }}
  >
    <i className={`fa ${icon}`} style={{ color, fontSize: 12 }} />
  </span>
);

const SubRowIcon = ({ info }: { info: { Icon: React.ElementType; iconBg: string; iconColor: string } }) => {
  const { Icon, iconBg, iconColor } = info;
  return (
    <span
      className={cn(
        'tw:inline-flex tw:items-center tw:justify-center tw:rounded tw:shrink-0',
        iconBg,
      )}
      style={{ width: 24, height: 24 }}
    >
      <Icon className={cn('tw:size-3.5', iconColor)} />
    </span>
  );
};
